/**
 * Damage code reference data for the IROPS Damage selection.
 *
 * Held here rather than inline in the component so the list is discoverable and
 * changeable without touching rendering logic. Order is significant — it is the
 * order controllers see in the dropdown.
 */

/** A single selectable damage code. */
export interface DamageCodeOption {
  value: string;
  label: string;
}

/** Damage codes rendered together beneath an `optgroup` label. */
export interface DamageCodeGroup {
  label: string;
  options: readonly DamageCodeOption[];
}

/** An entry in the damage code dropdown: either one code or a labelled group. */
export type DamageCodeEntry = DamageCodeOption | DamageCodeGroup;

/** Narrows a dropdown entry to a group so callers can render an `optgroup`. */
export function isDamageCodeGroup(entry: DamageCodeEntry): entry is DamageCodeGroup {
  return 'options' in entry;
}

/**
 * Seal Depressor codes share a stored prefix but display the shorter door
 * position, so the group label carries the shared context.
 */
const SEAL_DEPRESSOR_GROUP: DamageCodeGroup = {
  label: 'Seal Depressor Damage',
  options: [
    { value: 'Seal Depressor - Main Cargo Door - Fwd Upper', label: 'Main Cargo Door - Fwd Upper' },
    {
      value: 'Seal Depressor - Main Cargo Door - Fwd Middle',
      label: 'Main Cargo Door - Fwd Middle',
    },
    { value: 'Seal Depressor - Main Cargo Door - Fwd Lower', label: 'Main Cargo Door - Fwd Lower' },
    { value: 'Seal Depressor - Main Cargo Door - Aft Upper', label: 'Main Cargo Door - Aft Upper' },
    {
      value: 'Seal Depressor - Main Cargo Door - Aft Middle',
      label: 'Main Cargo Door - Aft Middle',
    },
    { value: 'Seal Depressor - Main Cargo Door - Aft Lower', label: 'Main Cargo Door - Aft Lower' },
    { value: 'Seal Depressor - Lower Cargo Door - Forward', label: 'Lower Cargo Door - Forward' },
    { value: 'Seal Depressor - Lower Cargo Door - Aft', label: 'Lower Cargo Door - Aft' },
  ],
};

/** Every damage code entry, in display order. */
export const DAMAGE_CODE_ENTRIES: readonly DamageCodeEntry[] = [
  { value: 'Bird Strike', label: 'Bird Strike' },
  { value: 'Cargo Spill', label: 'Cargo Spill' },
  { value: 'Damage Due to Water Intrusion', label: 'Damage Due to Water Intrusion' },
  { value: 'Damage during flight OPS', label: 'Damage during flight OPS' },
  { value: 'Equipment Strike Damage', label: 'Equipment Strike Damage' },
  { value: 'FOD Damage', label: 'FOD Damage' },
  { value: 'Ground Damage', label: 'Ground Damage' },
  { value: 'Ground Service', label: 'Ground Service' },
  { value: 'Hail Damage', label: 'Hail Damage' },
  { value: 'Lightning Strike', label: 'Lightning Strike' },
  { value: 'Loading/Unloading Damage', label: 'Loading/Unloading Damage' },
  { value: 'Maintenance Damage', label: 'Maintenance Damage' },
  { value: 'OPS/Crew Damage', label: 'OPS/Crew Damage' },
  { value: 'Other Damage', label: 'Other Damage' },
  { value: 'Other Damage (C)', label: 'Other Damage (C)' },
  { value: 'Previously Recorded Damage', label: 'Previously Recorded Damage' },
  SEAL_DEPRESSOR_GROUP,
  { value: 'Tail Strike', label: 'Tail Strike' },
  { value: 'Towing Damage', label: 'Towing Damage' },
];
